import React, { FC, useMemo, ElementType, useState, useEffect } from "react";
import { SwitchTransition } from "react-transition-group";

import classnames from "classnames";

import {
  StyledIcon,
  StyledSignifier,
  StyledSignifierTransition,
} from "./Styles";
import { Tooltip } from "../Tooltip";
import { Body1, Caption1 } from "../Typography";

export interface ISignifierProps {
  /** One of icon types */
  type: ElementType;
  /** Number that represent total value */
  value?: number;
  /** Value that represent class names */
  className?: string;
  /** The text that will be displayed on hover in tooltip */
  tooltipText?: string;
  /** ClassName for tooltip if there is one */
  tooltipClassName?: string;
}

export const signifierTypes = [
  "EyeIcon",
  "EyeSmallIcon",
  "PencilIcon",
  "PencilSmallIcon",
  "EyeOffIcon",
  "EyeOffSmallIcon",
  "ChecklistIcon",
  "ChecklistSmallIcon",
  "WarningTriangleIcon",
  "WarningTriangleSmallIcon",
  "DependencyIcon",
  "DependencySmallIcon",
  "MessageIcon",
  "MessageSmallIcon",
  "ClockStopwatchIcon",
  "ClockStopwatchSmallIcon",
  "ClockIcon",
  "ClockSmallIcon",
  "DollarOffIcon",
  "DollarOffSmallIcon",
  "DollarIcon",
  "DollarSmallIcon",
  "DollarCheckmarkIcon",
  "DollarCheckmarkSmallIcon",
  "DollarClockIcon",
  "DollarClockSmallIcon",
  "BellOffIcon",
  "BellOffSmallIcon",
  "LockIcon",
  "LockSmallIcon",
  "RecurringCheckmarkIcon",
  "RecurringCheckmarkSmallIcon",
  "PinIcon",
  "PinSmallIcon",
];

export const Signifier: FC<ISignifierProps> = ({
  type: Component,
  value = null,
  className,
  tooltipText,
  tooltipClassName,
}) => {
  if (
    signifierTypes.indexOf(
      (Component as { displayName: string }).displayName
    ) === -1
  ) {
    console.warn("You are using unsupported Signifier icon");
  }

  const [direction, setDirection] = useState("c-signifier--top");
  const [, setPrevValue] = useState(value);

  const label: string | null = useMemo(() => {
    if (!value || value < 1) return null;

    const max = 1_000_000_000;
    let newValue = value;

    if (newValue > max) {
      newValue = max;
    }

    if (newValue === max) {
      return `${Math.floor(newValue / 1_000_000_000)}B+`;
    } else if (newValue > 999_999) {
      return `${Math.floor(newValue / 1_000_000)}M`;
    } else if (newValue > 999) {
      return `${Math.floor(newValue / 1000)}k`;
    }

    return `${newValue}`;
  }, [value]);

  useEffect(() => {
    value &&
      setPrevValue((v) => {
        v &&
          setDirection(
            v - value > 0 ? "c-signifier--top" : "c-signifier--bottom"
          );
        return value;
      });
  }, [value]);

  const renderSignifier = useMemo(
    () => (
      <StyledSignifier
        className={classnames("c-signifier", className)}
        $direction={direction}
      >
        <StyledIcon as={Component} />
        {value !== null && (
          <SwitchTransition mode="out-in">
            <StyledSignifierTransition
              key={label}
              timeout={300}
              classNames="c-signifier--animate"
            >
              {(Component as { displayName: string }).displayName.includes(
                "SmallIcon"
              ) ? (
                <Caption1 color="secondary">{label}</Caption1>
              ) : (
                <Body1 color="secondary">{label}</Body1>
              )}
            </StyledSignifierTransition>
          </SwitchTransition>
        )}
      </StyledSignifier>
    ),
    [className, direction, Component, value, label]
  );

  return (
    <Tooltip
      title={tooltipText ?? ""}
      disable={!tooltipText}
      popperTooltipClassName={tooltipClassName}
    >
      {renderSignifier}
    </Tooltip>
  );
};

Signifier.displayName = "Signifier";
